<!--
+------------------------------------------------------------------+
| Author: Jeffrey Thumann                                          |
| Component: AncestorChromosomePainting                            |
| Section: components                                              |
| Purpose: Multi-tester chromosome painting by inferred ancestors. |
+------------------------------------------------------------------+
-->
<template>
    <article class="ancestor-painting">
        <p v-if="loading" class="state-message">{{ $t('geneticMatches.chromosomeBrowser.painting.loading') }}</p>
        <p v-else-if="error" class="state-message state-message--error">{{ error }}</p>
        <p v-else-if="assignments.length === 0" class="state-message">
            {{ $t('geneticMatches.chromosomeBrowser.painting.noAssignments') }}
        </p>
        <p v-else-if="visibleAssignments.length === 0" class="state-message">
            {{ $t('geneticMatches.chromosomeBrowser.painting.noAssignments') }}
        </p>

        <template v-else>
            <div class="tester-groups">
                <section v-for="tester in testerRows" :key="tester.testerCategory" class="tester-group">
                    <div class="tester-heading">
                        <h3>{{ tester.rootName || tester.testerCategory }}</h3>
                        <span class="tester-heading-meta">
                            <span>{{ tester.testerCategory }}</span>
                            <span class="painting-heading-metric painting-heading-metric--with-tooltip" tabindex="0">
                                {{ formatInteger(tester.assignmentCount) }} {{ $t('geneticMatches.chromosomeBrowser.painting.metrics.assignmentsShort') }}
                                <span class="painting-metric-tooltip">
                                    {{ $t('geneticMatches.chromosomeBrowser.painting.metrics.assignmentsTooltip', {
                                        count: formatInteger(tester.assignmentCount)
                                    }) }}
                                </span>
                            </span>
                            <span class="painting-heading-metric painting-heading-metric--with-tooltip" tabindex="0">
                                {{ formatOneDecimal(tester.mappedPercentage) }}% {{ $t('geneticMatches.chromosomeBrowser.painting.metrics.mappedPercentage') }}
                                <span class="painting-metric-tooltip">
                                    {{ $t('geneticMatches.chromosomeBrowser.painting.metrics.mappedTooltip', {
                                        cm: formatOneDecimal(tester.centimorgans)
                                    }) }}
                                </span>
                            </span>
                        </span>
                    </div>

                    <div class="chromosome-list">
                        <div v-for="chromosome in tester.chromosomes" :key="`${tester.testerCategory}-${chromosome.number}`"
                            class="chromosome-row">
                            <span class="chromosome-label">{{ chromosome.number }}</span>
                            <div class="chromosome-lanes" :style="{ width: chromosome.trackWidthPercent + '%' }">
                                <div v-for="lane in chromosome.lanes" :key="`${tester.testerCategory}-${chromosome.number}-${lane.side}`"
                                    class="chromosome-lane">
                                    <span class="chromosome-lane-label">{{ sideLabel(lane.side) }}</span>
                                    <div class="chromosome-track-shell">
                                        <div class="chromosome-track-base" />
                                        <div v-if="chromosome.unsampledPercent > 0" class="chromosome-track-unsampled"
                                            :style="{ width: chromosome.unsampledPercent + '%' }" />
                                        <button v-for="assignment in lane.assignments" :key="assignment.id"
                                            class="painting-segment"
                                            :class="`painting-segment--${assignment.confidence || 'suggested'}`"
                                            :style="assignmentStyle(assignment)" type="button"
                                            :aria-label="assignmentAriaLabel(assignment)"
                                            @mouseenter="showTooltip($event, assignment)" @mouseleave="hideTooltip"
                                            @focus="showTooltip($event, assignment)" @blur="hideTooltip" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </template>

        <div v-if="tooltip.visible" class="painting-tooltip" :style="{ left: tooltip.x + 'px', top: tooltip.y + 'px' }">
            <p><strong>{{ tooltip.assignment.ancestorLabel }}</strong></p>
            <p>{{ tooltip.assignment.matchName }} · {{ tooltip.assignment.testerCategory }}</p>
            <p>{{ sideLabel(tooltip.assignment.parentalSide) }}</p>
            <p>
                {{ $t('geneticMatches.chromosomeBrowser.tooltip.rangeMb') }}:
                {{ formatOneDecimal(tooltip.assignment.startMb) }}-{{ formatOneDecimal(tooltip.assignment.endMb) }} Mb
            </p>
            <p>{{ formatOneDecimal(tooltip.assignment.centimorgans) }} cM · {{ formatInteger(tooltip.assignment.snps) }} SNPs</p>
            <p>{{ confidenceLabel(tooltip.assignment.confidence) }}</p>
        </div>
    </article>
</template>

<script>
import {
    ACROCENTRIC_CHROMOSOMES,
    ACROCENTRIC_SHORT_ARM_BP,
    AUTOSOME_ORDER,
    CHROMOSOME_SIZES_HG19,
    MAX_AUTOSOME_BP_HG19,
} from '@/data/dna_segments/chromosomeSizesHg19';
import { colorForAncestorKey } from '@/utils/ancestorPaintingColors';

export default {
    name: 'AncestorChromosomePainting',
    props: {
        assignments: {
            type: Array,
            default: () => [],
        },
        testers: {
            type: Array,
            default: () => [],
        },
        selectedAncestorKey: {
            type: String,
            default: 'all',
        },
        autosomalCmTotalEstimate: {
            type: Number,
            default: 6800,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        error: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            tooltip: {
                visible: false,
                x: 0,
                y: 0,
                assignment: null,
            },
        };
    },
    computed: {
        visibleAssignments() {
            if (!this.selectedAncestorKey || this.selectedAncestorKey === 'all') {
                return this.assignments;
            }
            return this.assignments.filter(assignment => assignment.ancestorKey === this.selectedAncestorKey);
        },
        testerRows() {
            const testerMeta = new Map(this.testers.map(tester => [tester.testerCategory, tester]));
            const grouped = new Map();

            for (const assignment of this.visibleAssignments) {
                if (!grouped.has(assignment.testerCategory)) {
                    grouped.set(assignment.testerCategory, []);
                }
                grouped.get(assignment.testerCategory).push(assignment);
            }

            return [...grouped.entries()]
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([testerCategory, testerAssignments]) => {
                    const coverage = this.summarizeCoverage(testerAssignments);
                    const byChromosome = new Map();
                    for (const assignment of testerAssignments) {
                        if (!byChromosome.has(assignment.chromosome)) {
                            byChromosome.set(assignment.chromosome, []);
                        }
                        byChromosome.get(assignment.chromosome).push(assignment);
                    }

                    const chromosomes = AUTOSOME_ORDER.map((number) => {
                        const chromosomeLength = CHROMOSOME_SIZES_HG19[number];
                        const trackWidthPercent = (chromosomeLength / MAX_AUTOSOME_BP_HG19) * 100;
                        const unsampledBp = ACROCENTRIC_CHROMOSOMES.has(number) ? (ACROCENTRIC_SHORT_ARM_BP[number] || 0) : 0;
                        const unsampledPercent = chromosomeLength > 0 ? (unsampledBp / chromosomeLength) * 100 : 0;
                        const assignments = (byChromosome.get(number) || []).sort((a, b) => a.startBp - b.startBp);
                        const assignmentsBySide = {
                            paternal: assignments.filter(assignment => assignment.parentalSide === 'paternal'),
                            maternal: assignments.filter(assignment => assignment.parentalSide === 'maternal'),
                            unphased: assignments.filter(assignment => !['paternal', 'maternal'].includes(assignment.parentalSide)),
                        };
                        const lanes = [
                            { side: 'paternal', assignments: assignmentsBySide.paternal },
                            { side: 'maternal', assignments: assignmentsBySide.maternal },
                        ];
                        if (assignmentsBySide.unphased.length > 0) {
                            lanes.push({ side: 'unphased', assignments: assignmentsBySide.unphased });
                        }

                        return {
                            number,
                            trackWidthPercent,
                            unsampledPercent,
                            lanes,
                        };
                    });

                    return {
                        testerCategory,
                        rootName: testerMeta.get(testerCategory)?.rootName || testerCategory,
                        assignmentCount: testerAssignments.length,
                        centimorgans: coverage.centimorgans,
                        mappedPercentage: this.autosomalCmTotalEstimate > 0
                            ? (coverage.centimorgans / this.autosomalCmTotalEstimate) * 100
                            : 0,
                        chromosomes,
                    };
                });
        },
    },
    methods: {
        formatOneDecimal(value) {
            return Number(value || 0).toFixed(1);
        },
        formatInteger(value) {
            return new Intl.NumberFormat(this.$i18n?.locale?.value || undefined).format(Math.round(value || 0));
        },
        colorForAncestor(ancestorKey) {
            return colorForAncestorKey(ancestorKey);
        },
        summarizeCoverage(assignments) {
            const byTrack = new Map();

            for (const assignment of assignments) {
                const length = Math.max(Number(assignment.endBp || 0) - Number(assignment.startBp || 0), 1);
                const trackKey = [
                    assignment.parentalSide || 'unphased',
                    assignment.chromosome || 'unknown',
                ].join(':');
                if (!byTrack.has(trackKey)) {
                    byTrack.set(trackKey, []);
                }
                byTrack.get(trackKey).push({
                    startBp: Number(assignment.startBp || 0),
                    endBp: Number(assignment.endBp || 0),
                    cmPerBp: Number(assignment.centimorgans || 0) / length,
                });
            }

            let centimorgans = 0;
            for (const intervals of byTrack.values()) {
                const sorted = [...intervals]
                    .filter(interval => interval.endBp > interval.startBp)
                    .sort((a, b) => a.startBp - b.startBp || a.endBp - b.endBp);
                const covered = [];

                for (const interval of sorted) {
                    let cursor = interval.startBp;
                    for (const existing of covered) {
                        if (existing.endBp <= cursor) {
                            continue;
                        }
                        if (existing.startBp >= interval.endBp) {
                            break;
                        }
                        if (existing.startBp > cursor) {
                            centimorgans += (existing.startBp - cursor) * interval.cmPerBp;
                        }
                        cursor = Math.max(cursor, existing.endBp);
                        if (cursor >= interval.endBp) {
                            break;
                        }
                    }
                    if (cursor < interval.endBp) {
                        centimorgans += (interval.endBp - cursor) * interval.cmPerBp;
                    }

                    covered.push({ startBp: interval.startBp, endBp: interval.endBp });
                    covered.sort((a, b) => a.startBp - b.startBp || a.endBp - b.endBp);
                }
            }

            return { centimorgans };
        },
        assignmentStyle(assignment) {
            const chromosomeLength = CHROMOSOME_SIZES_HG19[assignment.chromosome] || 1;
            const left = (assignment.startBp / chromosomeLength) * 100;
            const width = Math.max(((assignment.endBp - assignment.startBp) / chromosomeLength) * 100, 0.45);
            return {
                left: `${left}%`,
                width: `${width}%`,
                background: this.colorForAncestor(assignment.ancestorKey),
            };
        },
        confidenceLabel(confidence) {
            const key = `geneticMatches.chromosomeBrowser.painting.confidence.${confidence || 'suggested'}`;
            return this.$te && this.$te(key) ? this.$t(key) : confidence;
        },
        sideLabel(side) {
            const normalizedSide = ['paternal', 'maternal'].includes(side) ? side : 'unphased';
            return this.$t(`geneticMatches.chromosomeBrowser.painting.sides.${normalizedSide}`);
        },
        assignmentAriaLabel(assignment) {
            return `${assignment.ancestorLabel}, ${this.sideLabel(assignment.parentalSide)}, chromosome ${assignment.chromosome}, ${this.formatOneDecimal(assignment.centimorgans)} cM, ${this.confidenceLabel(assignment.confidence)}.`;
        },
        showTooltip(event, assignment) {
            const rect = event.currentTarget.getBoundingClientRect();
            this.tooltip = {
                visible: true,
                x: rect.left + rect.width / 2,
                y: rect.top - 10,
                assignment,
            };
        },
        hideTooltip() {
            this.tooltip.visible = false;
        },
    },
};
</script>

<style scoped>
.ancestor-painting {
    border-radius: var(--border-radius-xl);
    background: var(--brand-bg-1);
    box-shadow: var(--shadow-card-sm);
    padding: 28px;
    min-width: 0;
}

.painting-metric-tooltip {
    position: absolute;
    right: 0;
    top: calc(100% + 8px);
    z-index: 40;
    width: min(260px, 70vw);
    border-radius: var(--border-radius-lg);
    background: rgba(34, 34, 34, 0.94);
    color: #fff !important;
    box-shadow: var(--shadow-card-sm);
    line-height: 1.35;
    opacity: 0;
    padding: 8px 10px;
    pointer-events: none;
    transform: translateY(-3px);
    transition: var(--transition-ease);
}

.painting-heading-metric--with-tooltip:hover .painting-metric-tooltip,
.painting-heading-metric--with-tooltip:focus-visible .painting-metric-tooltip {
    opacity: 1;
    transform: translateY(0);
}

.state-message {
    color: #777;
    margin: 20px 0 6px;
}

.state-message--error {
    color: var(--brand-color-1);
}

.tester-groups {
    display: flex;
    flex-direction: column;
    gap: 24px;
    margin-top: 18px;
}

.tester-group {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.tester-heading {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
    border-bottom: 1px solid #ececec;
    padding-bottom: 8px;
}

.tester-heading h3 {
    margin: 0;
    font-size: 16px;
}

.tester-heading span {
    color: #777;
    font-size: 12px;
}

.tester-heading-meta {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px 10px;
    justify-content: flex-end;
}

.painting-heading-metric--with-tooltip {
    cursor: help;
    position: relative;
}

.chromosome-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
    position: relative;
}

.chromosome-row {
    display: flex;
    align-items: flex-start;
    gap: 10px;
}

.chromosome-label {
    width: 20px;
    text-align: right;
    color: #666;
    font-size: 12px;
    flex-shrink: 0;
    padding-top: 14px;
}

.chromosome-lanes {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 40%;
}

.chromosome-lane {
    display: grid;
    grid-template-columns: 70px minmax(0, 1fr);
    align-items: center;
    gap: 8px;
}

.chromosome-lane-label {
    color: #777;
    font-size: 11px;
    line-height: 1;
    text-align: right;
}

.chromosome-track-shell {
    position: relative;
    height: 14px;
    min-width: 0;
}

.chromosome-track-base,
.chromosome-track-unsampled,
.painting-segment {
    position: absolute;
    top: 0;
    bottom: 0;
}

.chromosome-track-base {
    inset: 0;
    background: #ececec;
}

.chromosome-track-unsampled {
    left: 0;
    background: repeating-linear-gradient(45deg,
            #d8d8d8,
            #d8d8d8 4px,
            #f2f2f2 4px,
            #f2f2f2 8px);
}

.painting-segment {
    border: none;
    cursor: pointer;
    opacity: 0.9;
    transition: opacity 180ms ease, transform 180ms ease, outline-color 180ms ease;
}

.painting-segment--corroborated {
    opacity: 1;
}

.painting-segment--conflict {
    background-image: repeating-linear-gradient(135deg, rgba(255, 255, 255, 0.35) 0 4px, transparent 4px 8px);
}

.painting-segment:hover,
.painting-segment:focus {
    outline: 4px solid rgba(174, 30, 37, 0.25);
    transform: translateY(-1px);
    z-index: 3;
}

.painting-tooltip {
    position: fixed;
    transform: translate(-50%, -100%);
    background: #1f1f1f;
    color: #fff;
    border-radius: 8px;
    padding: 8px 10px;
    z-index: 45;
    pointer-events: none;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
    min-width: 220px;
}

.painting-tooltip p {
    margin: 0;
    font-size: 12px;
    line-height: 1.4;
}

@media (max-width: 860px) {
    .ancestor-painting {
        padding: 20px;
    }

    .chromosome-list {
        overflow-x: auto;
        padding-bottom: 4px;
    }

    .chromosome-lane {
        grid-template-columns: 64px minmax(0, 1fr);
    }

    .chromosome-lanes {
        min-width: 520px;
    }
}
</style>
